import type DateRange from '../../lib/dateRange';
import { type Measure, type MetricSeriesResult } from '../../types';

import { setupBatchCalculation, processBatchResults, executeBatchedQuery } from './core';

/**
 * Batch calculation for sum measures across multiple reporting periods.
 * Reduces query count from N to 1 query for N reporting periods.
 */

/**
 * Calculates sum measures using batch queries for multiple reporting periods.
 * This is the main optimization that reduces N queries to 1 query.
 */
export const calculateBatchedSumMeasures = async (
  measure: Measure | null,
  reportingPeriods: DateRange[],
): Promise<Omit<MetricSeriesResult, 'date'>[]> => {
  const setup = setupBatchCalculation(measure, reportingPeriods);

  if ('shouldReturn' in setup) {
    return reportingPeriods.map(() => ({ errors: { ...setup.errors }, result: { value: null } }));
  }

  const {
    errors,
    adjustedReportingPeriods,
    column,
    dateColumn,
    calculationWhereClause,
    periodSize,
    dataProvider,
    fiscalYearStartMonth,
  } = setup;

  try {
    // Execute single batched query using core function
    const batchData = await executeBatchedQuery(
      dataProvider,
      column,
      dateColumn,
      calculationWhereClause,
      adjustedReportingPeriods,
      periodSize,
      'sum',
      fiscalYearStartMonth,
    );

    return processBatchResults(batchData, adjustedReportingPeriods, periodSize, fiscalYearStartMonth, errors);
  } catch (error) {
    console.warn('Batch sum calculation failed:', error);
    throw error;
  }
};
